// @flow

import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import getClientTrackingParams from 'app/packages/client-tracking-params';
import getVideoFromPin from 'app/common/react/components/video/utils/getVideoFromPin';
import ImpressionTracker from 'app/common/lib/react/ImpressionTracker';
import pwaType from 'app/common/lib/pwaType';
import StatusPinRep from 'app/common/react/components/StatusPin/StatusPinRep';
import { isStatusPin } from 'app/common/lib/util/PinUtils';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { type Props as PinRepContainerProps } from 'app/common/react/components/PinRep/core/PinRepContainer';
import { type State as LocationState } from 'app/common/redux/reducers/location';
import { type State as ReduxState } from 'app/common/redux/types';
import PinRepConfiguration, {
  type OwnProps as PinRepConfigurationProps,
  type TrackingParameters,
} from 'app/common/react/components/PinRep/core/PinRepConfiguration';
import { getCommerceAuxData, getRichTypes } from 'app/common/lib/CommerceUtil';

type ReduxStateProps = {|
  location: LocationState,
  reduxPin: Pin,
|};

// NOTE TO DEVELOPERS: If you add a prop to PinRep, you do not have to add the
// same prop here; those props are copied into this component below via
// ...PinRepContainerProps. The only props that appear here are used by the
// "WithImpressions" part of PinRepWithImpressions, and the only that are listed
// in both are those used in both.
type OwnProps = {|
  blockClickEvents?: boolean,
  // Redux needs the pinId to fetch pin data.
  pinId: string,
  // Sadly some grids pass a non-standard pin object, so we need to allow for that. Ideally this goes away someday.
  pin?: Pin,

  // ImpressionTracker needs trackingParameters to operate.
  trackingParameters: TrackingParameters,
|};

export type Props = {|
  ...OwnProps,
  ...ReduxStateProps,
|};

// Temporarily export these props while experimental PinRep is in experiment.
export type ExternalProps = {|
  ...PinRepContainerProps,
  ...PinRepConfigurationProps,
  ...OwnProps,
|};

export function PinRepWithImpressions({
  blockClickEvents,
  location,
  reduxPin: pin,
  trackingParameters,
  ...pinRepConfigurationProps
}: Props) {
  // $FlowFixMe - Pin types don't match between PinRep and getClientTrackingParams.
  const clientTrackingParams = getClientTrackingParams(pin, location);
  const video = getVideoFromPin(pin);
  const isNativeVideoAndWatchable =
    video && video.url && !!video.height && !!video.width ? true : undefined;

  // Redundant check for pin.videos to appease flow
  const videoIdStr = video && pin.videos ? pin.videos.id : undefined;

  const [timingImpression, setTimingImpression] = useState(false);
  const handleLoad = useCallback(() => setTimingImpression(true), []);
  const handleError = useCallback(() => setTimingImpression(false), []);

  const {
    componentType,
    contextLogData,
    impressionAuxFields,
    slotIndex,
    viewData,
    viewParameter,
    viewType,
  } = trackingParameters;

  return (
    <ImpressionTracker
      componentType={componentType}
      contextLogData={{
        ...(typeof window !== 'undefined' ? { pwa_type: pwaType(window) } : {}),
        ...contextLogData,
        ...getCommerceAuxData(pin, contextLogData),
      }}
      loggingId={pin.id}
      impressionAuxFields={{
        ...(impressionAuxFields || {}),
        isNativeVideoAndWatchable,
        videoIdStr,
        clientTrackingParams: clientTrackingParams || `${pin.tracking_params || ''}-0`,
        imageURL: pin.images && pin.images['236x'].url,
        richTypes: getRichTypes(pin),
      }}
      impressionType="Pin"
      isPaused={!timingImpression}
      slotIndex={slotIndex}
      viewData={viewData}
      viewParameter={viewParameter}
      viewType={viewType}
    >
      {isStatusPin(pin) ? (
        <StatusPinRep pin={pin} />
      ) : (
        /* $FlowFixMe need to figure out property currentlyPlayingVideo is missing in props [1]
            but exists in ConnectedProps */
        <PinRepConfiguration
          {...pinRepConfigurationProps}
          blockClickEvents={blockClickEvents}
          debugImpressionState={timingImpression ? 'started' : 'paused'}
          onError={handleError}
          onLoad={handleLoad}
          pin={pin}
          trackingParameters={{
            componentType,
            contextLogData,
            slotIndex,
            viewParameter,
            viewType,
          }}
        />
      )}
    </ImpressionTracker>
  );
}

function mapStateToProps(
  { pins, location }: ReduxState,
  { pin: ownPin, pinId }: OwnProps,
): ReduxStateProps {
  return {
    location,
    // Sadly some grids pass a non-standard pin object, so we need to allow for that. Ideally this goes away someday.
    // $FlowFixMe: coercing different Pin types together.
    reduxPin: pins[pinId] || ownPin,
  };
}

export default connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  () => ({}),
)(PinRepWithImpressions);
